import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import BrandSelectPage from './pages/BrandSelectPage';
import MenuPage from './pages/MenuPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ConfirmationPage from './pages/ConfirmationPage';
import AuthPage from './pages/AuthPage';
import AccountPage from './pages/AccountPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import LegalPage from './pages/LegalPage';
import PayPalReturnPage from './pages/PayPalReturnPage';
import { CartProvider } from './context/CartContext';
import { OrderProvider, useOrder } from './context/OrderContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { BrandProvider } from './context/BrandContext';
import { logScanIfFirstVisit } from './services/scanTracker';

/** Legacy QR dine-in: auto-set mode + tableNumber from URL */
const DineInBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const match = window.location.pathname.match(/\/table\/(\d+)/);
  const { setMode, setTableNumber } = useOrder();

  React.useEffect(() => {
    if (match) {
      setMode('dine_in');
      setTableNumber(match[1]);
    }
  }, [match, setMode, setTableNumber]);

  return <>{children}</>;
};

/**
 * PromoBridge — captures `?promo=CODE` from the URL once per session.
 *
 * Used by the printed flyer campaigns (e.g. Lieferando-recapture FREECOLA).
 * The customer scans the QR on the flyer → lands here with `?promo=FREECOLA`
 * → we stash it in sessionStorage. CheckoutPage attaches it to the order
 * payload, and the submitQrOrder Cloud Function adds the free Cola.
 *
 * Cleared automatically: when the customer completes an order (cart cleared)
 * or when they explicitly remove it via UI.
 */
const PROMO_KEY = 'qr_active_promo';
const ALLOWED_PROMOS = new Set(['FREECOLA']);

const PromoBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = (params.get('promo') || '').toUpperCase().trim();
      if (code && ALLOWED_PROMOS.has(code)) {
        sessionStorage.setItem(PROMO_KEY, code);
        // eslint-disable-next-line no-console
        console.log('[PROMO] activated:', code);
      }
    } catch {
      /* private browsing without sessionStorage — silently skip */
    }
  }, []);
  return <>{children}</>;
};

/**
 * ScanTrackerBridge — logs one qr_scans Firestore document per session
 * (the scan that brought the visitor here). Distinguishes web vs table
 * (dine-in) by URL path. Read by the admin Marketing dashboard to surface
 * scan → order conversion per brand.
 */
const ScanTrackerBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  React.useEffect(() => {
    logScanIfFirstVisit();
  }, []);
  return <>{children}</>;
};

/** Require OTP verification — checks Firebase Auth OR OTP session (localStorage) */
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="page loading-page"><div className="spinner" /></div>;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location.pathname + location.search }} replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <LanguageProvider>
      <BrandProvider>
        <AuthProvider>
          <OrderProvider>
            <CartProvider>
              <DineInBridge>
                <PromoBridge>
                <ScanTrackerBridge>
                <Routes>
                  {/* Webshop flow */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/menu/:brand" element={<MenuPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/complete-profile" element={<RequireAuth><CompleteProfilePage /></RequireAuth>} />
                  {/* Account renders as overlay on TOP of the landing page — does NOT replace it */}
                  <Route path="/account" element={<RequireAuth><><LandingPage /><AccountPage /></></RequireAuth>} />
                  <Route path="/checkout" element={<RequireAuth><CheckoutPage /></RequireAuth>} />
                  <Route path="/confirmed/:orderNumber" element={<ConfirmationPage />} />
                  <Route path="/payment-return" element={<PayPalReturnPage />} />
                  <Route path="/orders" element={<RequireAuth><OrderTrackingPage /></RequireAuth>} />

                  {/* Legal pages — §5 TMG, DSGVO, AGB, Cookies */}
                  <Route path="/impressum" element={<LegalPage />} />
                  <Route path="/datenschutz" element={<LegalPage />} />
                  <Route path="/privacy" element={<LegalPage />} />
                  <Route path="/agb" element={<LegalPage />} />
                  <Route path="/terms" element={<LegalPage />} />
                  <Route path="/cookies" element={<LegalPage />} />
                  <Route path="/legal/:kind" element={<LegalPage />} />

                  {/* Legacy QR dine-in flow (table-based, no auth needed) */}
                  <Route path="/table/:tableNumber" element={<BrandSelectPage />} />
                  <Route path="/table/:tableNumber/menu/:brand" element={<MenuPage />} />
                  <Route path="/table/:tableNumber/cart" element={<CartPage />} />
                  <Route path="/table/:tableNumber/confirmed/:orderNumber" element={<ConfirmationPage />} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </ScanTrackerBridge>
                </PromoBridge>
              </DineInBridge>
            </CartProvider>
          </OrderProvider>
        </AuthProvider>
      </BrandProvider>
    </LanguageProvider>
  );
}

export default App;
